import {
    createContext,
    ReactNode,
    useEffect,
    useState,
} from 'react';

import PropTypes from 'prop-types';
import {fetchUserAttributes} from 'aws-amplify/auth';
import {useAuth} from "../hooks/useAuth.ts";
import {signOutViaBroker} from "../utils/signOutViaBroker.ts";

type UserAttributesContextType = {
    givenName: string,
    familyName: string,
    name: string
    email: string,
    companyName: string,
    tier: string
}

// FNP-958: a context object and its provider component live together by
// design; splitting them would churn every import for a dev-only
// hot-reload nicety. Same suppression billing-app uses on the identical
// file (its AuthContext.tsx:25), so this matches platform precedent.
// eslint-disable-next-line react-refresh/only-export-components
export const UserAttributesContext = createContext<UserAttributesContextType>({
                                                                                  givenName: '',
                                                                                  familyName: '',
                                                                                  name: '',
                                                                                  email: '',
                                                                                  companyName: '',
                                                                                  tier: '',
                                                                              });


type UserAttributesContextProviderProps = {
    children?: ReactNode;
}

export const UserAttributesContextProvider = ({
                                                  children,
                                              }: UserAttributesContextProviderProps) => {


    const [givenName, setGivenName] = useState('')
    const [familyName, setFamilyName] = useState('')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [tier, setTier] = useState('')

    const {
        user
    } = useAuth()


    useEffect(() => {
        const fetchAttributes = async () => {
            if (!user) {
                return
            }

            // FNP-958: Cognito user attributes are string-valued and every consumer
            // below feeds them to a setState<string>, so `string` is the accurate
            // type — `any` was hiding that the shape was already known.
            let attributes = {} as { [key: string]: string | undefined }
            try {
                attributes = await fetchUserAttributes();
            } catch (e: unknown) {
                if (e instanceof Error) {
                    switch (e.name) {
                        case 'NotAuthorizedException':
                            console.debug('Message', e.message)
                            if (e.message.includes('Token is inactive')) {
                                await signOutViaBroker()
                            }
                            break
                    }
                }
            }


            if (Array.isArray(Object.keys(attributes)) && Object.keys(attributes).length > 0) {

                Object.keys(attributes)
                      .forEach((key: string) => {

                          switch (key) {
                              case 'given_name':
                                  setGivenName(attributes[key] ?? '')
                                  break
                              case 'family_name':
                                  setFamilyName(attributes[key] ?? '')
                                  break
                              case 'name':
                                  setName(attributes[key] ?? '')
                                  break
                              case 'email':
                                  setEmail(attributes[key] ?? '')
                                  break
                              case 'custom:company_name':
                                  setCompanyName(attributes[key] ?? '')
                                  break
                              case 'custom:tier':
                                  setTier(attributes[key] ?? '')
                                  break
                          }
                      })

            }

        }

        fetchAttributes()

    }, [user])


    if (!user) {
        return children
    }

    return (
        <UserAttributesContext.Provider
            value={{
                givenName,
                familyName,
                name,
                email,
                companyName,
                tier
            }}
        >
            {children}
        </UserAttributesContext.Provider>
    );
};

UserAttributesContextProvider.propTypes = {
    children: PropTypes.node,
};

